import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  AlertCircle, Loader2, Sparkles, Globe, MapPin, Shield, Clock, Star, CheckCircle,
  AlertTriangle, ArrowLeftRight, DollarSign, Target, Brain, Download, ChevronDown,
  ChevronUp, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { addPdfHeader, addPdfFooter } from '../../utils/pdfGenerator';
import { getDescriptionQuality, COUNTRIES } from './utils';
import { apiPost, ApiError } from '../../lib/apiClient';

export default function SourcingTab({ user, setUser, setActiveTab }) {
  const navigate = useNavigate();

  // Smart Sourcing state
  const [sourcingForm, setSourcingForm] = useState({
    description: '',
    country_of_origin: 'China',
    import_country: 'United States',
    region_preference: 'any',
    max_lead_time: 'any',
    fta_only: false
  });
  const [sourcingLoading, setSourcingLoading] = useState(false);
  const [sourcingResults, setSourcingResults] = useState(null);
  const [sourcingEnhanceLoading, setSourcingEnhanceLoading] = useState(false);
  const [expandedCountry, setExpandedCountry] = useState(null);


  const handleSourcingEnhance = async () => {
    if (!sourcingForm.description || sourcingForm.description.trim().length < 3) {
      toast.error('Please enter a basic description first');
      return;
    }
    setSourcingEnhanceLoading(true);
    try {
      const data = await apiPost('/api/ai/enhance-description', { description: sourcingForm.description });
      if (data?.enhanced_description) {
        setSourcingForm({ ...sourcingForm, description: data.enhanced_description });
        toast.success('Description enhanced with technical details!');
      } else {
        toast.error('Enhancement failed');
      }
    } catch (error) {
      if (error instanceof ApiError) toast.error(error.message || 'Enhancement failed');
      else toast.error('Connection error');
    } finally {
      setSourcingEnhanceLoading(false);
    }
  };

  // Handle Smart Sourcing analysis

  const handleSourcingAnalysis = async (e) => {
    e.preventDefault();
    
    if (!sourcingForm.description || !sourcingForm.country_of_origin) {
      toast.error('Please fill in product description and origin country');
      return;
    }

    setSourcingLoading(true);
    setSourcingResults(null);

    try {
      const data = await apiPost('/api/analyze/sourcing', {
        description: sourcingForm.description,
        country_of_origin: sourcingForm.country_of_origin,
        import_country: sourcingForm.import_country,
        region_preference: sourcingForm.region_preference !== 'any' ? sourcingForm.region_preference : null,
        max_lead_time: sourcingForm.max_lead_time !== 'any' ? sourcingForm.max_lead_time : null,
        fta_only: sourcingForm.fta_only,
      });
      setSourcingResults(data);
      toast.success('Sourcing analysis complete!');
    } catch (error) {
      if (error instanceof ApiError) toast.error(error.message || 'Analysis failed');
      else toast.error('Connection error. Please try again.');
    } finally {
      setSourcingLoading(false);
    }
  };

  // Export sourcing analysis as PDF

  const handleSourcingPdfExport = () => {
    if (!sourcingResults) return;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const w = pdf.internal.pageSize.getWidth();
    const margin = 15;
    let y = 30; // Start below header

    const checkPage = () => { if (y > 265) { pdf.addPage(); y = 30; } };
    const sectionTitle = (text) => {
      checkPage();
      pdf.setFontSize(13); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 41, 59);
      pdf.text(text, margin, y); y += 7;
      pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
    };
    const bodyText = (text, maxW) => {
      const lines = pdf.splitTextToSize(text, maxW || (w - 2 * margin));
      pdf.setTextColor(71, 85, 105);
      pdf.text(lines, margin, y); y += lines.length * 4 + 2;
    };

    // Product info
    sectionTitle('Smart Sourcing Analysis');
    bodyText(sourcingForm.description);
    pdf.setTextColor(100, 116, 139);
    pdf.text(`Origin: ${sourcingForm.country_of_origin}  →  Import to: ${sourcingForm.import_country}  |  ${new Date().toLocaleDateString()}`, margin, y);
    y += 8;

    // Current situation
    const current = sourcingResults.ai_analysis;
    if (current) {
      sectionTitle(`Current Situation: ${current.country}`);
      pdf.setTextColor(220, 38, 38); pdf.text(`Effective Tariff: ${current.effective_tariff_rate}%`, margin, y); y += 5;
      pdf.setTextColor(71, 85, 105);
      if (current.assessment) bodyText(current.assessment);
      if (current.risks?.length > 0) {
        pdf.setTextColor(180, 83, 9); pdf.setFont('helvetica', 'bold');
        pdf.text('Risks:', margin, y); y += 5; pdf.setFont('helvetica', 'normal');
        current.risks.forEach(r => { pdf.text(`  - ${r}`, margin, y); y += 4; });
        y += 3;
      }
    }

    // Recommendations
    (sourcingResults.recommendations || []).forEach((rec) => {
      checkPage();
      sectionTitle(`#${rec.rank} ${rec.country}${rec.rank === 1 ? '  (Best Match)' : ''}`);
      pdf.setTextColor(5, 150, 105);
      pdf.text(`Tariff: ${rec.effective_tariff_rate}%   |   Savings: +${rec.tariff_savings_percentage?.toFixed(1)}%   |   Score: ${rec.recommendation_score}/100`, margin, y);
      y += 6;
      pdf.setTextColor(71, 85, 105);
      if (rec.trade_agreement) { pdf.text(`Trade Agreement: ${rec.trade_agreement}`, margin, y); y += 5; }
      if (rec.lead_time) { pdf.text(`Lead Time: ${rec.lead_time}   |   Quality: ${rec.quality_tier}`, margin, y); y += 5; }
      if (rec.annual_savings_estimate) { pdf.setTextColor(37, 99, 235); pdf.text(`Potential Annual Savings: $${rec.annual_savings_estimate.toLocaleString()}`, margin, y); y += 5; }
      pdf.setTextColor(71, 85, 105);
      if (rec.manufacturing_fit) bodyText(`Manufacturing Fit: ${rec.manufacturing_fit}`);
      if (rec.pros?.length > 0) { pdf.setTextColor(5, 150, 105); rec.pros.forEach(p => { pdf.text(`  + ${p}`, margin, y); y += 4; }); }
      if (rec.cons?.length > 0) { pdf.setTextColor(220, 38, 38); rec.cons.forEach(c => { pdf.text(`  - ${c}`, margin, y); y += 4; }); }
      pdf.setTextColor(71, 85, 105);
      if (rec.implementation_notes) { checkPage(); bodyText(`How to switch: ${rec.implementation_notes}`); }
      y += 4;
    });

    // Summary
    const summary = sourcingResults.optimization_summary;
    if (summary?.key_insight) {
      checkPage(); sectionTitle('Summary');
      bodyText(summary.key_insight);
      if (summary.max_annual_savings) {
        pdf.setTextColor(37, 99, 235); pdf.setFont('helvetica', 'bold');
        pdf.text(`Maximum Annual Savings: $${summary.max_annual_savings.toLocaleString()}`, margin, y);
        y += 5;
      }
    }

    // Apply branding header/footer to all pages
    const pages = pdf.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      pdf.setPage(p);
      addPdfHeader(pdf, p, pages);
      addPdfFooter(pdf, p === pages);
    }

    pdf.save(`decoded-sourcing-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('PDF downloaded!');
  };

  // Save sourcing analysis to history

  const handleSaveSourcing = async () => {
    if (!sourcingResults) return;
    try {
      await apiPost('/api/sourcing/save', {
        description: sourcingForm.description,
        country_of_origin: sourcingForm.country_of_origin,
        import_country: sourcingForm.import_country,
        ...sourcingResults,
      });
      toast.success('Sourcing analysis saved to history!');
    } catch (error) {
      if (error instanceof ApiError) toast.error(error.message || 'Failed to save');
      else toast.error('Connection error');
    }
  };

  // Handle Origin Validation

  return (
          /* Smart Sourcing Tab Content */
          <div className="space-y-6">
            {/* Top row: Form + AI Summary side by side */}
            <div className="grid lg:grid-cols-5 gap-6">
              {/* Sourcing Input Form - 2 cols */}
              <div className="lg:col-span-2 card-modern p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#06B6D4] flex items-center justify-center shadow-lg">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-outfit text-lg font-semibold text-slate-900">Find Best Sourcing Countries</h2>
                    <p className="text-sm text-slate-500 font-jakarta">Enter product details to optimize</p>
                  </div>
                </div>

                <form onSubmit={handleSourcingAnalysis} className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-700 font-jakarta font-medium">Product Description *</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={handleSourcingEnhance}
                        disabled={sourcingEnhanceLoading || !sourcingForm.description || sourcingForm.description.trim().length < 3}
                        className="text-xs h-7 px-2 text-[#10B981] hover:text-[#059669] hover:bg-[#10B981]/10"
                        data-testid="sourcing-ai-enhance-btn">
                        {sourcingEnhanceLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                        AI Enhance
                      </Button>
                    </div>
                    <Textarea placeholder="e.g., Wireless Bluetooth Headphones with Noise Cancellation"
                      value={sourcingForm.description}
                      onChange={(e) => setSourcingForm({ ...sourcingForm, description: e.target.value })}
                      className="min-h-[80px] input-modern resize-none" required data-testid="sourcing-description-input" />
                    {sourcingForm.description && (
                      <div className={`flex items-center gap-2 text-xs ${
                        getDescriptionQuality(sourcingForm.description).color === 'green' ? 'text-green-600' :
                        getDescriptionQuality(sourcingForm.description).color === 'amber' ? 'text-amber-600' : 'text-red-500'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          getDescriptionQuality(sourcingForm.description).color === 'green' ? 'bg-green-500' :
                          getDescriptionQuality(sourcingForm.description).color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                        }`} />
                        {getDescriptionQuality(sourcingForm.description).message}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-jakarta font-medium text-sm">Current Origin *</Label>
                      <Select value={sourcingForm.country_of_origin}
                        onValueChange={(value) => setSourcingForm({ ...sourcingForm, country_of_origin: value })}>
                        <SelectTrigger className="input-modern" data-testid="sourcing-origin-select"><SelectValue placeholder="Select country" /></SelectTrigger>
                        <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-jakarta font-medium text-sm">Import Market</Label>
                      <Select value={sourcingForm.import_country}
                        onValueChange={(value) => setSourcingForm({ ...sourcingForm, import_country: value })}>
                        <SelectTrigger className="input-modern" data-testid="sourcing-import-select"><SelectValue placeholder="Select market" /></SelectTrigger>
                        <SelectContent>
                          <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Markets</div>
                          <SelectItem value="United States">United States (HTS)</SelectItem>
                          <SelectItem value="European Union">European Union (CN)</SelectItem>
                          <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                          <div className="border-t border-slate-100 my-1" />
                          {COUNTRIES.filter(c => !['United States', 'United Kingdom'].includes(c)).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-3 space-y-3">
                    <p className="text-xs font-medium text-slate-600 font-jakarta">Filters</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Region</Label>
                        <Select value={sourcingForm.region_preference}
                          onValueChange={(value) => setSourcingForm({ ...sourcingForm, region_preference: value })}>
                          <SelectTrigger className="h-9 text-sm" data-testid="sourcing-region-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">All Regions</SelectItem>
                            <SelectItem value="americas">Americas</SelectItem>
                            <SelectItem value="asia">Asia</SelectItem>
                            <SelectItem value="europe">Europe</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Lead Time</Label>
                        <Select value={sourcingForm.max_lead_time}
                          onValueChange={(value) => setSourcingForm({ ...sourcingForm, max_lead_time: value })}>
                          <SelectTrigger className="h-9 text-sm" data-testid="sourcing-leadtime-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="fast">Fast Only</SelectItem>
                            <SelectItem value="standard">Standard or Faster</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                      <input type="checkbox" checked={sourcingForm.fta_only}
                        onChange={(e) => setSourcingForm({ ...sourcingForm, fta_only: e.target.checked })}
                        className="w-3.5 h-3.5 rounded border-slate-300 text-[#10B981] focus:ring-[#10B981]/20"
                        data-testid="sourcing-fta-checkbox" />
                      <Shield className="w-3.5 h-3.5 text-[#6366F1]" />
                      FTA countries only
                    </label>
                  </div>

                  <Button type="submit" className="w-full btn-primary-modern py-5 bg-gradient-to-r from-[#10B981] to-[#06B6D4] hover:opacity-90"
                    disabled={sourcingLoading} data-testid="sourcing-analyze-btn">
                    {sourcingLoading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Analyzing sourcing options...</>
                    ) : (
                      <><Globe className="w-5 h-5 mr-2" />Find Best Countries</>
                    )}
                  </Button>
                </form>
              </div>

              {/* Right panel: AI Narrative + Current Situation - 3 cols */}
              <div className="lg:col-span-3 space-y-5">
                {!sourcingResults ? (
                  <div className="card-modern p-8 h-full flex flex-col items-center justify-center text-center">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#10B981]/10 to-[#06B6D4]/10 flex items-center justify-center mb-5">
                      <Globe className="w-10 h-10 text-[#10B981]/40" />
                    </div>
                    <h3 className="font-outfit text-lg font-semibold text-slate-400 mb-2">Sourcing Intelligence</h3>
                    <p className="text-sm text-slate-400 max-w-sm">Enter a product description and run the analysis to get AI-powered sourcing recommendations with tariff optimization.</p>
                  </div>
                ) : (
                  <>
                    {/* AI Narrative Summary — prominent headline */}
                    {sourcingResults.optimization_summary && (
                      <div className="card-modern p-5 bg-gradient-to-r from-[#10B981]/5 to-[#06B6D4]/5 border-[#10B981]/20" data-testid="sourcing-ai-summary">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-xl bg-[#10B981] flex items-center justify-center flex-shrink-0">
                            <Brain className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-outfit font-semibold text-slate-900 text-base mb-1">
                              {sourcingResults.optimization_summary.best_alternative || sourcingResults.optimization_summary.best_alternative_country} is your best alternative
                              {sourcingResults.optimization_summary.max_annual_savings && (
                                <span className="ml-2 text-[#10B981] font-bold">
                                  — save ${sourcingResults.optimization_summary.max_annual_savings.toLocaleString()}/yr
                                </span>
                              )}
                            </p>
                            {sourcingResults.optimization_summary.key_insight && (
                              <p className="text-sm text-slate-600 leading-relaxed">{sourcingResults.optimization_summary.key_insight}</p>
                            )}
                            {sourcingResults.optimization_summary.risk_considerations && (
                              <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                                {sourcingResults.optimization_summary.risk_considerations}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            {sourcingResults.metadata?.ai_powered && (
                              <span className="text-[10px] bg-[#10B981]/10 text-[#10B981] px-2 py-1 rounded-full font-medium whitespace-nowrap flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> AI Powered
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Current Situation Analysis */}
                    {sourcingResults.ai_analysis && (
                      <div className="card-modern p-5" data-testid="sourcing-current-analysis">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-red-500" />
                          </div>
                          <div>
                            <h3 className="font-outfit font-semibold text-slate-900 text-sm">Current: {sourcingResults.ai_analysis.country}</h3>
                            <p className="text-xs text-slate-500">Your existing sourcing situation</p>
                          </div>
                          <div className="ml-auto bg-red-50 px-3 py-1.5 rounded-lg">
                            <span className="text-sm font-bold text-red-600">{sourcingResults.ai_analysis.effective_tariff_rate}%</span>
                            <span className="text-xs text-red-400 ml-1">effective tariff</span>
                          </div>
                        </div>

                        {/* Tariff breakdown tags */}
                        {sourcingResults.ai_analysis.tariff_breakdown?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {sourcingResults.ai_analysis.tariff_breakdown.map((item, i) => (
                              <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">{item}</span>
                            ))}
                          </div>
                        )}

                        {/* Assessment */}
                        {sourcingResults.ai_analysis.assessment && (
                          <p className="text-sm text-slate-600 mb-3 leading-relaxed">{sourcingResults.ai_analysis.assessment}</p>
                        )}

                        {/* Risks */}
                        {sourcingResults.ai_analysis.risks?.length > 0 && (
                          <div className="bg-amber-50/60 rounded-lg p-3">
                            <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5" /> Identified Risks
                            </p>
                            <div className="space-y-1">
                              {sourcingResults.ai_analysis.risks.map((risk, i) => (
                                <p key={i} className="text-xs text-amber-800 flex items-start gap-2">
                                  <span className="text-amber-400 mt-0.5">-</span> {risk}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Side-by-Side Comparison: Current vs Best */}
                    {sourcingResults.recommendations?.length > 0 && sourcingResults.ai_analysis && (
                      <div className="card-modern p-5" data-testid="sourcing-comparison">
                        <h3 className="font-outfit font-semibold text-slate-900 text-sm mb-4 flex items-center gap-2">
                          <ArrowLeftRight className="w-4 h-4 text-[#6366F1]" /> Quick Comparison
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-slate-500 border-b border-slate-100">
                                <th className="text-left pb-2 pr-4 font-medium"></th>
                                <th className="text-center pb-2 px-3 font-medium">
                                  <span className="text-red-500">{sourcingResults.ai_analysis.country}</span>
                                  <span className="text-[10px] text-slate-400 block">Current</span>
                                </th>
                                <th className="text-center pb-2 px-3 font-medium">
                                  <span className="text-[#10B981]">{sourcingResults.recommendations[0].country}</span>
                                  <span className="text-[10px] text-slate-400 block">Recommended</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="text-xs">
                              <tr className="border-b border-slate-50">
                                <td className="py-2.5 pr-4 text-slate-500">Effective Tariff</td>
                                <td className="py-2.5 px-3 text-center font-semibold text-red-600">{sourcingResults.ai_analysis.effective_tariff_rate}%</td>
                                <td className="py-2.5 px-3 text-center font-semibold text-[#10B981]">{sourcingResults.recommendations[0].effective_tariff_rate}%</td>
                              </tr>
                              <tr className="border-b border-slate-50">
                                <td className="py-2.5 pr-4 text-slate-500">Trade Agreement</td>
                                <td className="py-2.5 px-3 text-center text-slate-400">—</td>
                                <td className="py-2.5 px-3 text-center">
                                  {sourcingResults.recommendations[0].trade_agreement ? (
                                    <span className="bg-[#6366F1]/10 text-[#6366F1] px-2 py-0.5 rounded-full text-[10px] font-medium">{sourcingResults.recommendations[0].trade_agreement}</span>
                                  ) : <span className="text-slate-400">—</span>}
                                </td>
                              </tr>
                              <tr className="border-b border-slate-50">
                                <td className="py-2.5 pr-4 text-slate-500">Lead Time</td>
                                <td className="py-2.5 px-3 text-center text-slate-600">{sourcingResults.ai_analysis.lead_time || 'Standard'}</td>
                                <td className="py-2.5 px-3 text-center text-slate-600">{sourcingResults.recommendations[0].lead_time}</td>
                              </tr>
                              <tr className="border-b border-slate-50">
                                <td className="py-2.5 pr-4 text-slate-500">Quality Tier</td>
                                <td className="py-2.5 px-3 text-center text-slate-600">{sourcingResults.ai_analysis.quality_tier || 'High'}</td>
                                <td className="py-2.5 px-3 text-center text-slate-600">{sourcingResults.recommendations[0].quality_tier}</td>
                              </tr>
                              <tr>
                                <td className="py-2.5 pr-4 text-slate-500">Tariff Savings</td>
                                <td className="py-2.5 px-3 text-center text-slate-400">—</td>
                                <td className="py-2.5 px-3 text-center font-bold text-[#2563EB]">+{sourcingResults.recommendations[0].tariff_savings_percentage?.toFixed(1)}%</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Full-width: Detailed Country Recommendations */}
            {sourcingResults?.recommendations?.length > 0 && (
              <div className="space-y-4" data-testid="sourcing-recommendations">
                <div className="flex items-center justify-between">
                  <h3 className="font-outfit text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#10B981]" /> Alternative Countries
                  </h3>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleSaveSourcing}
                      className="text-xs gap-1.5" data-testid="sourcing-save-history-btn">
                      <FileText className="w-3.5 h-3.5" /> Save to History
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleSourcingPdfExport}
                      className="text-xs gap-1.5" data-testid="sourcing-export-pdf-btn">
                      <Download className="w-3.5 h-3.5" /> Export PDF
                    </Button>
                  </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-5">
                  {sourcingResults.recommendations.map((rec, idx) => {
                    const isExpanded = expandedCountry === idx;
                    return (
                      <div key={idx}
                        className={`card-modern overflow-hidden transition-all duration-200 ${
                          idx === 0 ? 'ring-2 ring-[#10B981]/30 shadow-lg shadow-[#10B981]/5' : ''
                        }`}
                        data-testid={`sourcing-result-${idx}`}>

                        {/* Card header */}
                        <div className={`px-5 py-4 ${idx === 0 ? 'bg-gradient-to-r from-[#10B981]/5 to-[#06B6D4]/5' : 'bg-slate-50/50'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2.5">
                              <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                                idx === 0 ? 'bg-[#10B981] text-white' : idx === 1 ? 'bg-[#2563EB] text-white' : 'bg-slate-300 text-white'
                              }`}>#{rec.rank || idx + 1}</span>
                              <span className="font-outfit font-semibold text-slate-900 text-lg">{rec.country}</span>
                            </div>
                            {idx === 0 && (
                              <span className="bg-[#10B981] text-white text-[10px] px-2.5 py-1 rounded-full font-semibold tracking-wide uppercase">
                                Best Match
                              </span>
                            )}
                          </div>

                          {/* Key metrics */}
                          <div className="grid grid-cols-3 gap-2 mt-3">
                            <div className="bg-white rounded-lg px-3 py-2 text-center">
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Tariff</p>
                              <p className="text-sm font-bold text-[#10B981]">{rec.effective_tariff_rate}%</p>
                            </div>
                            <div className="bg-white rounded-lg px-3 py-2 text-center">
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Savings</p>
                              <p className="text-sm font-bold text-[#2563EB]">+{rec.tariff_savings_percentage?.toFixed(1)}%</p>
                            </div>
                            <div className="bg-white rounded-lg px-3 py-2 text-center">
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Score</p>
                              <p className="text-sm font-bold text-slate-700">{rec.recommendation_score}<span className="text-[10px] text-slate-400">/100</span></p>
                            </div>
                          </div>
                        </div>

                        {/* Card body */}
                        <div className="px-5 py-4 space-y-3">
                          {/* Annual savings callout */}
                          {rec.annual_savings_estimate && (
                            <div className="flex items-center gap-2 bg-[#2563EB]/5 rounded-lg px-3 py-2">
                              <DollarSign className="w-4 h-4 text-[#2563EB]" />
                              <span className="text-sm font-semibold text-[#2563EB]">${rec.annual_savings_estimate.toLocaleString()}</span>
                              <span className="text-xs text-slate-500">potential annual savings</span>
                            </div>
                          )}

                          {/* Manufacturing fit */}
                          {rec.manufacturing_fit && (
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Manufacturing Fit</p>
                              <p className="text-xs text-slate-700 leading-relaxed">{rec.manufacturing_fit}</p>
                            </div>
                          )}

                          {/* Tags */}
                          <div className="flex flex-wrap gap-1.5">
                            {rec.trade_agreement && (
                              <span className="flex items-center gap-1 bg-[#6366F1]/10 text-[#6366F1] px-2 py-1 rounded-full text-[10px] font-medium">
                                <Shield className="w-3 h-3" /> {rec.trade_agreement}
                              </span>
                            )}
                            <span className="flex items-center gap-1 bg-slate-100 text-slate-600 px-2 py-1 rounded-full text-[10px]">
                              <Clock className="w-3 h-3" /> {rec.lead_time}
                            </span>
                            <span className="flex items-center gap-1 bg-slate-100 text-slate-600 px-2 py-1 rounded-full text-[10px]">
                              <Star className="w-3 h-3" /> {rec.quality_tier}
                            </span>
                          </div>

                          {/* Pros */}
                          {rec.pros?.length > 0 && (
                            <div className="space-y-1">
                              {rec.pros.map((pro, i) => (
                                <p key={i} className="text-xs text-[#10B981] flex items-start gap-1.5">
                                  <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {pro}
                                </p>
                              ))}
                            </div>
                          )}

                          {/* Cons */}
                          {rec.cons?.length > 0 && (
                            <div className="space-y-1">
                              {rec.cons.map((con, i) => (
                                <p key={i} className="text-xs text-red-500 flex items-start gap-1.5">
                                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {con}
                                </p>
                              ))}
                            </div>
                          )}

                          {/* Expand/collapse for implementation notes */}
                          {rec.implementation_notes && (
                            <div>
                              <button onClick={() => setExpandedCountry(isExpanded ? null : idx)}
                                className="flex items-center gap-1.5 text-xs text-[#2563EB] hover:text-[#1D4ED8] font-medium transition-colors"
                                data-testid={`sourcing-expand-${idx}`}>
                                {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                {isExpanded ? 'Hide' : 'How to switch'}
                              </button>
                              {isExpanded && (
                                <div className="mt-2 bg-[#2563EB]/5 rounded-lg p-3 animate-in fade-in duration-200">
                                  <p className="text-[10px] text-[#2563EB] uppercase tracking-wide font-semibold mb-1">Implementation Guide</p>
                                  <p className="text-xs text-slate-700 leading-relaxed">{rec.implementation_notes}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Score bar */}
                          <div className="pt-2 border-t border-slate-100">
                            <div className="flex items-center justify-between text-[10px] mb-1">
                              <span className="text-slate-400">Recommendation Score</span>
                              <span className="font-bold text-slate-600">{rec.recommendation_score}/100</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-[#10B981] to-[#06B6D4] rounded-full transition-all duration-500"
                                style={{ width: `${rec.recommendation_score}%` }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
  );
}
